package org.junqueira.portfolio.data

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable

object Strings {

    private val language: Language
        @Composable
        @ReadOnlyComposable
        get() = LocalLanguage.current

    // --- Navigation ---

    val projects: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Projects"
            Language.PT -> "Projetos"
        }

    val skills: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Skills"
            Language.PT -> "Competências"
        }

    val about: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "About"
            Language.PT -> "Sobre"
        }

    val contact: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Contact"
            Language.PT -> "Contacto"
        }

    val getInTouch: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Get in Touch"
            Language.PT -> "Entrar em Contacto"
        }

    // --- Profile ---

    val profileGreeting: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Hi there, my name is Alfredo Junqueira, Android Developer"
            Language.PT -> "Olá, o meu nome é Alfredo Junqueira, Programador Android"
        }

    val profileSubtitle: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "I build clean and user friendly Android apps, always with a focus on great experience."
            Language.PT -> "Construo apps Android limpas e intuitivas, sempre com foco numa excelente experiência."
        }

    val viewMyWork: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "View My Work"
            Language.PT -> "Ver Meu Trabalho"
        }

    // --- Skills ---

    val skillsTitle: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Skills & Technologies"
            Language.PT -> "Competências & Tecnologias"
        }

    // Skill card titles
    val androidDevelopment: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Android Development"
            Language.PT -> "Desenvolvimento Android"
        }

    val architecture: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Architecture"
            Language.PT -> "Arquitetura"
        }

    val databaseBackend: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Database & Backend"
            Language.PT -> "Base de Dados & Backend"
        }

    val modernTools: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Modern Tools"
            Language.PT -> "Ferramentas Modernas"
        }

    val testing: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Testing"
            Language.PT -> "Testes"
        }

    val additionalSkills: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Additional Skills"
            Language.PT -> "Competências Adicionais"
        }

    // --- About Me ---

    val aboutMe: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "About Me"
            Language.PT -> "Sobre Mim"
        }

    val aboutMeText: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "My name is Alfredo Junqueira, I'm an Android developer with hands-on experience building native applications in Kotlin, using Jetpack Compose, MVVM, and REST integrations to deliver fluid and high-performance interfaces. My work with mobile testing has strengthened my ability to analyze logs, identify failures, and improve application reliability, resulting in reduced recurring errors and more stable builds. In personal and academic projects, I develop complete components, from UI to navigation, status, caching, and local persistence. I am constantly dedicated to learning and clean code, seeking to create scalable and well-structured solutions. I always strive to transform technical knowledge into real impact for the user and the team.\nSee what I'm building or contact me to collaborate on something new."
            Language.PT -> "O meu nome é Alfredo Junqueira, sou programador Android com experiência prática na construção de aplicações nativas em Kotlin, utilizando Jetpack Compose, MVVM e integrações REST para entregar interfaces fluidas e de alto desempenho. O meu trabalho com testes mobile fortaleceu a minha capacidade de analisar logs, identificar falhas e melhorar a fiabilidade das aplicações, resultando em menos erros recorrentes e builds mais estáveis. Em projetos pessoais e académicos, desenvolvo componentes completos, desde UI até navegação, estado, cache e persistência local. Sou constantemente dedicado à aprendizagem e ao código limpo, procurando criar soluções escaláveis e bem estruturadas. Esforço-me sempre por transformar conhecimento técnico em impacto real para o utilizador e a equipa.\nVeja o que estou a construir ou contacte-me para colaborar em algo novo."
        }

    // --- Contact ---

    val letsConnect: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Let's Connect"
            Language.PT -> "Vamos Conectar"
        }

    val connectSubtitle: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Have a project in mind or just want to say hi? Feel free to reach out."
            Language.PT -> "Tem um projeto em mente ou quer apenas dizer olá? Não hesite em contactar."
        }

    val nameLabel: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Name"
            Language.PT -> "Nome"
        }

    val emailLabel: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Email"
            Language.PT -> "Email"
        }

    val messageLabel: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Message"
            Language.PT -> "Mensagem"
        }

    val sendMessage: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Send Message"
            Language.PT -> "Enviar Mensagem"
        }

    // --- Projects ---

    val seeMore: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "See more"
            Language.PT -> "Ver mais"
        }

    val viewOnGithub: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "View on GitHub"
            Language.PT -> "Ver no GitHub"
        }

    val demo: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Demo"
            Language.PT -> "Demo"
        }

    val last: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "Last"
            Language.PT -> "Último"
        }

    // --- Bottom Bar ---

    val copyright: String
        @Composable @ReadOnlyComposable
        get() = when (language) {
            Language.EN -> "© 2025 Alfredo Junqueira. All Rights Reserved."
            Language.PT -> "© 2025 Alfredo Junqueira. Todos os Direitos Reservados."
        }
}
